<template>
  <SelectButton
    v-model="option"
    :options="options"
    option-label="label"
    option-value="value"
    :multiple="multiple"
    option-disabled="disabled"
    :unselectable="allowEmpty"
    :class="{ 'card-style-buttons': cardStyle }"
  >
    <template #option="slotProps">
      <!-- Card style layout -->
      <template v-if="cardStyle">
        <div class="button-card-content">
          <div class="button-card-icon">
            <i
              v-if="slotProps.option.icon"
              class="far"
              :class="slotProps.option.icon"
            />
            <span
              v-else-if="slotProps.option.svg"
              v-html="slotProps.option.svg"
              class="spm_icon"
            />
            <img
              v-else-if="slotProps.option.src"
              :src="slotProps.option.src"
              class="spm_icon"
            >
          </div>
          <span class="button-card-label">
            {{ slotProps.option.title ?? slotProps.option.label }}
          </span>
        </div>
      </template>
      <!-- Default inline layout -->
      <template v-else>
        <div class="button-ghost">
          <i
            v-if="slotProps.option.icon"
            class="far"
            :class="slotProps.option.icon"
          />
          <img
            v-else-if="slotProps.option.src"
            :src="slotProps.option.src"
            class="spm_icon"
          >
          <span
            v-else
            class="p-button-label"
          >
          {{ slotProps.option.title ?? slotProps.option.label }}
        </span>
        </div>

        <div class="button-tooltip" v-tooltip="slotProps.option.label">
          <i
            v-if="slotProps.option.icon"
            class="far"
            :class="slotProps.option.icon"
            data-test-id="button"
          />
          <img
            v-else-if="slotProps.option.src"
            :src="slotProps.option.src"
            class="spm_icon"
            data-test-id="button"
          >
          <span
            v-else
            class="p-button-label"
            data-test-id="button"
          >
            {{ slotProps.option.title ?? slotProps.option.label }}
          </span>
        </div>
      </template>
    </template>
  </SelectButton>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  PropType,
  Ref,
  ref,
  SetupContext,
  watch,
} from 'vue';
import SelectButton from 'primevue/selectbutton';
import Tooltip from 'primevue/tooltip';
import { StringMap } from '@/types';

export default defineComponent({
  name: 'BaseButtonGroup',

  components: {
    SelectButton,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: [Array, String, Number, Boolean] as PropType<string[] | string | number | boolean>,
      required: true,
    },

    options: {
      type: Object as PropType<StringMap>,
      required: true,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    allowEmpty: {
      type: Boolean,
      default: true,
    },

    cardStyle: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }: SetupContext) {
    let option: Ref;
    if (props.multiple && Array.isArray(props.modelValue)) {
      option = ref([]);
    } else {
      option = ref(props.modelValue);
    }

    watch([option], () => emit('update:modelValue', option.value));

    watch(() => props.modelValue, () => {
      if (!props.multiple && !Array.isArray(props.modelValue)) {
        option.value = props.modelValue;
      }
    });

    onMounted(() => {
      if (props.multiple && Array.isArray(props.modelValue)) {
        props.modelValue.forEach((value) => {
          option.value.push(value);
        });
      }
    });

    return {
      option,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(.button-tooltip) {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: auto; // permet le tooltip
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.button-ghost) {
  visibility: hidden; // cache l'élément mais il prend la place
  pointer-events: none;
}

:deep(.spm_icon) {
  width: 1.4rem;
  min-height: 1.01rem;
  filter: $heather-filter-for-icons;
}

:deep(.p-button.p-highlight .spm_icon) {
  filter: none !important;
}

.p-selectbutton {
  display: flex;

  :deep(.p-button) {
    font-size: 14px !important;
    background-color: $white;
    flex-grow: 1;
    color: $tuna !important;
    box-shadow: none !important;
    outline: none !important;
    border: 1px solid $heather !important;
    border-left: none !important;
    height: $field-default-height;

    &:first-child {
      border-left: 1px solid $heather !important;
    }

    &:hover:not(.p-highlight) {
      background-color: #f2f2f2 !important;
    }

    &.p-highlight {
      background-color: $brand-color-primary;
      color: $white !important;

      &:hover {
        background-color: $brand-color-primary;
      }
    }
  }
}

// Card style layout

.configuration-wrapper .p-panel > .p-toggleable-content .p-panel-content .field-group .field-group-content .p-selectbutton.card-style-buttons {
  &.p-selectbutton {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 12px !important;

    :deep(.p-button) {
      flex: 0 0 auto !important;
      min-width: 90px !important;
      height: auto !important;
      padding: 10px 12px !important;
      background-color: #fff !important;
      border: 3px solid #e0e0e0 !important;
      border-radius: 8px !important;
      box-shadow: none !important;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;

      &:hover:not(.p-highlight) {
        background-color: #fafafa !important;
        border-color: #d0d0d0 !important;
      }

      &.p-highlight {
        background-color: #f4f9eb !important;
        border: 3px solid #94c840 !important;
        padding: 9px 11px !important;

        .button-card-icon {
          color: #94c840 !important;
        }

        .button-card-label {
          color: #94c840 !important;
        }
      }

      .button-card-content {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        gap: 10px !important;
      }

      .button-card-icon {
        font-size: 2.2rem !important;
        color: #6c757d !important;

        .spm_icon {
          width: 36px !important;
          height: 36px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;

          :deep(svg) {
            width: 100% !important;
            height: 100% !important;
          }
        }
      }

      .button-card-label {
        font-size: 0.75rem !important;
        font-weight: 400 !important;
        text-transform: uppercase !important;
        color: #6c757d !important;
        white-space: nowrap !important;
      }
    }
  }
}
</style>
